import { Box } from "@mui/material";
import { FC, useEffect, useMemo } from "react";
import Select from "@/components/Select/Select";
import { Programs } from "@/domain/programs.interface";
import { SkeletonFilters } from "./skeletons";

export type SiteOfficeHoursFilters = {
  programId: string;
  orgUnitId: string;
};

export const defaultSiteOfficeHoursFilters: SiteOfficeHoursFilters = {
  programId: "",
  orgUnitId: "",
};

type Props = {
  filtersState: [
    SiteOfficeHoursFilters,
    React.Dispatch<React.SetStateAction<SiteOfficeHoursFilters>>,
  ];
  programs: Programs[] | undefined;
  isLoadingPrograms: boolean;
  isFetchingPrograms: boolean;
};

export const SiteOfficeHoursFiltersComponent: FC<Props> = ({
  filtersState,
  programs,
  isLoadingPrograms,
  isFetchingPrograms,
}) => {
  const [filters, setFilters] = filtersState;

  const programsOptions = useMemo(() => {
    if (!programs) return [];
    return programs.map((p) => ({ key: p._id, value: p.name }));
  }, [programs]);

  const selectedProgram = useMemo(() => {
    if (!programs || !filters.programId) return null;
    return programs.find((p) => p._id === filters.programId) ?? null;
  }, [programs, filters.programId]);

  const orgUnitsOptions = useMemo(() => {
    if (!selectedProgram) return [];
    return selectedProgram.orgUnitIds
      .filter((ou) => ou.status === "ACTIVE")
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((ou) => ({ key: ou._id, value: ou.name }));
  }, [selectedProgram]);

  // Pre-select first program on load
  useEffect(() => {
    if (programs && programs.length > 0 && !filters.programId) {
      const firstProgram = programs[0];
      const firstActiveOrgUnit = firstProgram.orgUnitIds
        .filter((ou) => ou.status === "ACTIVE")
        .sort((a, b) => a.name.localeCompare(b.name))[0];

      setFilters({
        programId: firstProgram._id,
        orgUnitId: firstActiveOrgUnit?._id ?? "",
      });
    }
  }, [programs]);

  // When program changes, select first org unit
  const handleProgramChange = (value: string) => {
    const program = programs?.find((p) => p._id === value);
    const firstActiveOrgUnit = program?.orgUnitIds
      .filter((ou) => ou.status === "ACTIVE")
      .sort((a, b) => a.name.localeCompare(b.name))[0];

    setFilters({
      programId: value,
      orgUnitId: firstActiveOrgUnit?._id ?? "",
    });
  };

  if (isFetchingPrograms || isLoadingPrograms) {
    return <SkeletonFilters />;
  }

  return (
    <Box sx={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
      <Select
        handleChange={(value) => handleProgramChange(value as string)}
        data={programsOptions}
        value={filters.programId}
        label="Program"
        id="site-office-hours-program"
        isLoading={isLoadingPrograms}
        maxWidth={320}
        fullWidth
      />
      <Select
        handleChange={(value) =>
          setFilters((prev) => ({ ...prev, orgUnitId: value as string }))
        }
        data={orgUnitsOptions}
        value={filters.orgUnitId}
        label="Site"
        id="site-office-hours-site"
        maxWidth={320}
        fullWidth
      />
    </Box>
  );
};
